import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@/lib/router-compat";
import { ShieldCheck, MapPin } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useVisitorCity, distanceKm } from "@/hooks/useVisitorCity";
import { CityPicker } from "@/components/listings/detail/SellerLocationCard";
import { useDealerUrl } from '@/hooks/useDealerUrl';

interface CardDealerStripProps {
  userId?: string | null;
  listingCity?: string | null;
  dealerVerified?: boolean;
}

function normalize(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ș/g, "s").replace(/ț/g, "t").replace(/ă/g, "a").replace(/â/g, "a").replace(/î/g, "i")
    .trim();
}

function useDealerInfo(userId?: string | null) {
  return useQuery({
    queryKey: ["dealer-info", userId],
    enabled: !!userId,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    queryFn: async () => {
      const { data } = await supabase
        .from("dealer_profiles")
        .select("business_name, verified, risco_verified, logo_url")
        .eq("user_id", userId!)
        .maybeSingle();
      return data
        ? {
            name: (data as any).business_name as string | null,
            logo: (data as any).logo_url as string | null,
            verified: !!(data as any).verified || !!(data as any).risco_verified,
          }
        : null;
    },
  });
}

function usePublicDealerName(userId?: string | null) {
  return useQuery({
    queryKey: ["verified-dealer-public-names", "RO"],
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_verified_dealers_public", { p_country_code: "RO" });
      if (error) throw error;
      return new Map((data || []).map((row: any) => [row.user_id as string, row.business_name as string | null]));
    },
  });
}

function useCityCoords(city?: string | null) {
  const norm = city ? normalize(city) : "";
  return useQuery({
    queryKey: ["city-coords", norm],
    enabled: !!norm,
    staleTime: 60 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    queryFn: async () => {
      const { data } = await supabase
        .from("geo_ro_city_county")
        .select("latitude, longitude")
        .eq("city_norm", norm)
        .not("latitude", "is", null)
        .limit(1);
      if (data && data.length > 0 && data[0].latitude != null) {
        return { lat: Number(data[0].latitude), lng: Number(data[0].longitude) };
      }
      return null;
    },
  });
}

export function CardDealerStrip({ userId, listingCity, dealerVerified }: CardDealerStripProps) {
  const dealerUrl = useDealerUrl(userId);
  const { data: dealer, isLoading: dealerLoading } = useDealerInfo(userId);
  const { data: publicNames } = usePublicDealerName(userId);
  const { data: coords } = useCityCoords(listingCity);
  const { city: visitorCity, setCity } = useVisitorCity();
  const [pickerOpen, setPickerOpen] = useState(false);

  const distance =
    visitorCity && coords ? Math.round(distanceKm(visitorCity, coords)) : null;

  const sellerName =
    dealer?.name ||
    (userId ? publicNames?.get(userId) : undefined) ||
    (userId && dealerLoading ? "" : "Vânzător privat");
  const verified = dealerVerified ?? dealer?.verified ?? false;
  const initial = sellerName.charAt(0).toUpperCase();

  const openPicker = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setPickerOpen(true);
  };

  const inner = (
    <div className="flex items-start gap-3 px-4 py-3 bg-card">
      {/* Logo / fallback initial — larger, rounded-sm square like detail page */}
      {dealer?.logo ? (
        <img
          src={dealer.logo}
          alt={sellerName}
          loading="lazy"
          className="w-12 h-12 rounded-lg object-cover bg-muted border border-border flex-shrink-0"
        />
      ) : (
        <div className="w-12 h-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center font-bold text-lg flex-shrink-0">
          {initial}
        </div>
      )}

      <div className="flex-1 min-w-0">
        {/* Name + Verified pill */}
        <div className="flex items-center gap-2 flex-wrap">
          <h4 className="text-sm font-bold text-foreground truncate max-w-full">
            {sellerName}
          </h4>
          {verified && (
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-brand-green-foreground bg-brand-green px-2 py-0.5 rounded">
              <ShieldCheck className="w-3 h-3" />
              Verificat
            </span>
          )}
        </div>

        {/* City */}
        <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
          <MapPin className="w-3.5 h-3.5 text-primary/70 flex-shrink-0" />
          <span className="truncate">{listingCity || "Locație nespecificată"}</span>
        </div>

        {/* Distance is shown here; the city selector appears once above the listing grid. */}
        {distance !== null ? (
          <button
            type="button"
            onClick={openPicker}
            className="mt-1 inline-flex items-center gap-1 text-xs text-foreground font-medium hover:text-primary"
          >
            <MapPin className="w-3 h-3 text-primary" />
            la {distance} km de tine · schimbă
          </button>
        ) : null}
      </div>
    </div>
  );

  return (
    <>
      {dealerUrl ? (
        <Link
          to={dealerUrl}
          onClick={(e) => e.stopPropagation()}
          className="block hover:bg-muted/40 transition-colors"
        >
          {inner}
        </Link>
      ) : (
        inner
      )}

      {pickerOpen && (
        <CityPicker
          current={visitorCity}
          onClose={() => setPickerOpen(false)}
          onSelect={(c) => { setCity(c); setPickerOpen(false); }}
          onClear={() => { setCity(null); setPickerOpen(false); }}
        />
      )}
    </>
  );
}
